import { JSX, useState, useEffect, useCallback } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import DrumPicker from './DrumPicker';

function isoToDate(iso: string): Date | null {
  if (!iso) {
    return null;
  }
  const [year, month, day] = iso.split('-').map(Number);
  if (!year || !month || !day) {
    return null;
  }
  return new Date(year, month - 1, day);
}

function dateToIso(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

interface Props {
  value: string; // ISO "YYYY-MM-DD" or ""
  onChange: (v: string) => void;
}

function DesktopPicker({ value, onChange }: Props): JSX.Element {
  const [pickerValue, setPickerValue] = useState<Date | null>(() =>
    isoToDate(value)
  );

  useEffect(() => {
    setPickerValue(isoToDate(value));
  }, [value]);

  const handleChange = useCallback(
    (date: Date | null) => {
      if (!date || isNaN(date.getTime())) {
        setPickerValue(null);
        return;
      }
      setPickerValue(date);
      onChange(dateToIso(date));
    },
    [onChange]
  );

  return (
    <Box data-testid="date-input">
      <DesktopDatePicker
        value={pickerValue}
        format="MM/dd/yyyy"
        onChange={handleChange}
        slotProps={{
          textField: {
            size: 'small',
            fullWidth: true,
            sx: {
              '& .MuiOutlinedInput-root': {
                borderRadius: '16px',
                bgcolor: 'background.paper',
                '& fieldset': {
                  borderColor: 'divider',
                },
              },
            },
          },
        }}
      />
    </Box>
  );
}

export default function DateInput({ value, onChange }: Props): JSX.Element {
  const isDesktop = useMediaQuery('(min-width:960px)');

  if (isDesktop) {
    return <DesktopPicker value={value} onChange={onChange} />;
  }

  return (
    <Box data-testid="date-input">
      <DrumPicker value={value} onChange={onChange} />
    </Box>
  );
}
